import { jwtDecode } from 'jwt-decode';
import { isEqual } from 'lodash';
import { log } from '@frameio/utils/log';

// const DEBUG_QUERY_PARAM_DIFFERENCES = true;
const DEBUG_QUERY_PARAM_DIFFERENCES = false;

/** These query params can be ignored if they change across API repsonses as
 * long as the cached URL is still valid
 **/
const IGNORED_QUERY_PARAMS = ['x-amz-meta-request_id', 'Signature'];

type isMediaUrlDifferentOrExpiringType = {
  /**
   * the existing URL value.
   */
  current: string;
  /**
   * the incoming URL value.
   */
  next: string;
  /**
   * which param in the URL to evaluate, as well as an expression for handling
   * related logic.
   */
  param?: 'token' | 'Expires';
};

const isExpiringWithinFiveMinutes = (expiration: number) => {
  const fiveMinutesAsMilliseconds = 5 * 60 * 1000;

  return new Date() >= new Date(expiration * 1000 - fiveMinutesAsMilliseconds);
};

const isDifferent = ({
  currentUrl,
  nextUrl,
  param: expiresOrTokenParam,
}: {
  currentUrl: URL;
  nextUrl: URL;
  param: string;
}) => {
  // ignore the param we will use for expiration check
  currentUrl.searchParams.delete(expiresOrTokenParam);
  nextUrl.searchParams.delete(expiresOrTokenParam);

  for (const param of IGNORED_QUERY_PARAMS) {
    currentUrl.searchParams.delete(param);
    nextUrl.searchParams.delete(param);
  }

  const areEqual = currentUrl.toString() === nextUrl.toString();

  // Expensive debug logging, so you must opt-in with the DEBUG_QUERY_PARAM_DIFFERENCES flag at the top of this file
  if (DEBUG_QUERY_PARAM_DIFFERENCES && !areEqual) {
    const currentUrlBase = currentUrl.origin + currentUrl.pathname;
    const nextUrlBase = nextUrl.origin + nextUrl.pathname;
    if (currentUrlBase === nextUrlBase) {
      log.debug(
        `[mediaUrl] incoming URL has different base: ${currentUrlBase}, ${nextUrlBase}`
      );
    } else {
      const currentParams = Array.from(currentUrl.searchParams.entries());

      const differentParams = currentParams.filter(([key, value]) => {
        const nextValue = nextUrl.searchParams.get(key);
        return value !== nextValue;
      });

      if (differentParams.length > 0) {
        log.debug(
          `[mediaUrl] incoming URL has different query params\n${nextUrl.toString()}`
        );
        differentParams.forEach(([key, oldValue]) => {
          const newValue = nextUrl.searchParams.get(key);
          log.debug(`  ${key}: ${oldValue} -> ${newValue}`);
        });
      }
    }
  }
  return !areEqual;
};

const isExpiring = ({
  currentSearchParams,
  nextSearchParams,
  param,
}: {
  currentSearchParams: URLSearchParams;
  nextSearchParams: URLSearchParams;
  param: string;
}) => {
  const existingParamValue = currentSearchParams.get(param);
  const incomingParamValue = nextSearchParams.get(param);

  // If we cannot compare, assume the image is different
  if (!existingParamValue || !incomingParamValue) {
    log.debug(
      '[mediaUrl] Either existing or incoming URL did not have the required query param that would allow us to cache it.'
    );
    return true;
  }

  /**
   * Not all image URL strings have the same params. This switch allows for
   * various checks depending on the `param` prop.
   */
  switch (param) {
    case 'token': {
      const { exp: currentExpiration, sub: currentSub } = jwtDecode(
        existingParamValue
      ) as any;
      const { sub: nextSub } = jwtDecode(incomingParamValue) as any;

      // If expiring soon, doesn't matter if they are equal
      if (isExpiringWithinFiveMinutes(currentExpiration)) {
        log.debug(
          "[mediaUrl] existing URL's token is expired or expiring within five minutes."
        );
        return true;
      }

      // `sub` can be a string or an object
      if (!isEqual(currentSub, nextSub)) {
        log.debug(
          '[mediaUrl] existing token.sub is different from incoming token.sub'
        );
        return true;
      }

      return false;
    }

    /**
     * This case accounts for changed/expiring resource expiration.
     */
    case 'Expires': {
      if (isExpiringWithinFiveMinutes(Number(existingParamValue))) {
        log.debug(
          '[mediaUrl] existing signed URL is expired or expiring within five minutes.'
        );
        return true;
      }

      return false;
    }

    default: {
      return true;
    }
  }
};

/**
 * Verifies if two provided URL strings with a token parameter
 * (eg. picture service URLs) are different, or about to expire.
 * @param {Object} obj
 * @param {string} obj.current
 * @param {string} obj.next
 * @param {string} obj.param
 * @returns {boolean}
 */
export const isMediaUrlDifferentOrExpiring = ({
  current,
  next,
  param = 'token',
}: isMediaUrlDifferentOrExpiringType) => {
  if (!current || !next) return true;

  try {
    const currentUrl = new URL(current);
    const nextUrl = new URL(next);

    // Copy search params, isDifferent will mutate the original URL object
    const currentSearchParams = new URLSearchParams(currentUrl.searchParams);
    const nextSearchParams = new URLSearchParams(nextUrl.searchParams);
    if (isDifferent({ currentUrl, nextUrl, param })) return true;
    if (isExpiring({ currentSearchParams, nextSearchParams, param })) {
      return true;
    }

    return false;
  } catch (error) {
    return true;
  }
};
